import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, Save, Calendar, ChevronLeft, ChevronRight, 
  Upload, RefreshCw, Trash2, Plus, Copy, Check, Download, Pencil
} from 'lucide-react';

import { API } from '../../lib/api';

export default function AdminPricing() {
  const { token } = useAuth();
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [pricePeriods, setPricePeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Calendario
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  
  // Form nuovo prezzo
  const [priceForm, setPriceForm] = useState({
    price: '',
    minStay: 1,
    syncToBeds24: true
  });
  
  // Form bulk (più periodi)
  const [bulkForm, setBulkForm] = useState({
    periods: [
      { startDate: '', endDate: '', price: '', minStay: 1 }
    ]
  });

  // Stato per modifica periodo
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [unitsRes, periodsRes] = await Promise.all([
        axios.get(`${API}/admin/units`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/price-periods`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setUnits(unitsRes.data);
      setPricePeriods(periodsRes.data || []);
      if (unitsRes.data.length > 0) {
        setSelectedUnit(unitsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Elimina periodo
  const deletePeriod = async () => {
    if (!periodToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/price-periods/${periodToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Periodo eliminato');
      setDeleteConfirmOpen(false);
      setPeriodToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  // Modifica periodo
  const updatePeriod = async () => {
    if (!editingPeriod) return;
    
    setSyncing(true);
    try {
      // Aggiorna nel database locale
      await axios.put(`${API}/admin/price-periods/${editingPeriod.id}`, {
        unit_id: editingPeriod.unit_id || selectedUnit?.id,
        nome_periodo: editingPeriod.nome_periodo || `Periodo ${editingPeriod.data_inizio} - ${editingPeriod.data_fine}`,
        data_inizio: editingPeriod.data_inizio,
        data_fine: editingPeriod.data_fine,
        prezzo_notte: parseFloat(editingPeriod.prezzo_notte),
        soggiorno_minimo: parseInt(editingPeriod.soggiorno_minimo) || 1
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Sync su Beds24
      await axios.post(`${API}/admin/beds24/prices/sync`, {
        date_from: editingPeriod.data_inizio,
        date_to: editingPeriod.data_fine,
        price: parseFloat(editingPeriod.prezzo_notte),
        min_stay: parseInt(editingPeriod.soggiorno_minimo) || 1
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('✓ Periodo aggiornato e sincronizzato su Beds24');
      setEditDialogOpen(false);
      setEditingPeriod(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSyncing(false);
    }
  };


  // Genera giorni del mese
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Padding per iniziare dal lunedì
    const startPadding = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Giorni del mese
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  }, [currentMonth]);

  // Trova il prezzo per una data
  const getPriceForDate = (date) => {
    if (!date) return null;
    const dateStr = date.toISOString().split('T')[0];
    
    for (const period of pricePeriods) {
      if (dateStr >= period.data_inizio && dateStr <= period.data_fine) {
        return period;
      }
    }
    return null;
  };

  // Seleziona/deseleziona data
  const toggleDate = (date) => {
    if (!date) return;
    const dateStr = date.toISOString().split('T')[0];
    
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr].sort());
    }
  };

  // Seleziona range
  const selectRange = (start, end) => {
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    setSelectedDates(dates);
  };

  // Salva prezzo per date selezionate
  const savePriceForDates = async () => {
    if (selectedDates.length === 0 || !priceForm.price) {
      toast.error('Seleziona date e inserisci il prezzo');
      return;
    }
    
    setSyncing(true);
    try {
      // Trova il range di date
      const sortedDates = [...selectedDates].sort();
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];
      
      // Salva nel backend locale
      await axios.post(`${API}/admin/price-periods`, {
        unit_id: selectedUnit?.id,
        nome_periodo: `Periodo ${startDate} - ${endDate}`,
        data_inizio: startDate,
        data_fine: endDate,
        prezzo_notte: parseFloat(priceForm.price),
        soggiorno_minimo: parseInt(priceForm.minStay)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Sync su Beds24 se richiesto
      if (priceForm.syncToBeds24) {
        await axios.post(`${API}/admin/beds24/prices/sync`, {
          date_from: startDate,
          date_to: endDate,
          price: parseFloat(priceForm.price),
          min_stay: parseInt(priceForm.minStay)
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        toast.success(`✓ Prezzo €${priceForm.price} salvato e sincronizzato su Beds24`);
      } else {
        toast.success(`✓ Prezzo €${priceForm.price} salvato localmente`);
      }
      
      // Reset
      setSelectedDates([]);
      setPriceForm({ price: '', minStay: 1, syncToBeds24: true });
      setPriceDialogOpen(false);
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSyncing(false);
    }
  };

  // Salva più periodi insieme
  const saveBulkPrices = async () => {
    const validPeriods = bulkForm.periods.filter(p => p.startDate && p.endDate && p.price);
    
    if (validPeriods.length === 0) {
      toast.error('Inserisci almeno un periodo valido');
      return;
    }
    
    setSyncing(true);
    let success = 0;
    let failed = 0;
    
    for (const period of validPeriods) {
      try {
        // Salva locale
        await axios.post(`${API}/admin/price-periods`, {
          unit_id: selectedUnit?.id,
          nome_periodo: `Periodo ${period.startDate} - ${period.endDate}`,
          data_inizio: period.startDate,
          data_fine: period.endDate,
          prezzo_notte: parseFloat(period.price),
          soggiorno_minimo: parseInt(period.minStay) || 1
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        // Sync Beds24
        await axios.post(`${API}/admin/beds24/prices/sync`, {
          date_from: period.startDate,
          date_to: period.endDate,
          price: parseFloat(period.price),
          min_stay: parseInt(period.minStay) || 1
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        success++;
      } catch {
        failed++;
      }
    }
    
    setSyncing(false);
    setBulkDialogOpen(false);
    setBulkForm({ periods: [{ startDate: '', endDate: '', price: '', minStay: 1 }] });
    fetchData();
    
    if (failed > 0) {
      toast.warning(`${success} salvati, ${failed} errori`);
    } else {
      toast.success(`✓ ${success} periodi salvati e sincronizzati su Beds24`);
    }
  };

  // Importa prezzi da Beds24
  const importFromBeds24 = async () => {
    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/admin/beds24/import-prices`,
        { months_ahead: 6 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`✓ ${response.data.message}`);
        fetchData(); // Ricarica i periodi
      } else {
        toast.error(response.data.message || 'Errore importazione');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore importazione da Beds24');
    } finally {
      setImporting(false);
    }
  };

  // Aggiungi periodo al form bulk
  const addBulkPeriod = () => {
    setBulkForm({
      periods: [...bulkForm.periods, { startDate: '', endDate: '', price: '', minStay: 1 }]
    });
  };

  // Rimuovi periodo dal form bulk
  const removeBulkPeriod = (index) => {
    setBulkForm({
      periods: bulkForm.periods.filter((_, i) => i !== index)
    });
  };

  // Copia prezzo dal periodo precedente
  const copyPreviousPeriod = (index) => {
    if (index === 0) return;
    const prev = bulkForm.periods[index - 1];
    const newPeriods = [...bulkForm.periods];
    newPeriods[index] = { ...newPeriods[index], price: prev.price, minStay: prev.minStay };
    setBulkForm({ periods: newPeriods });
  };

  // Formatta mese
  const formatMonth = (date) => {
    return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  // Determina colore cella calendario
  const getCellColor = (date) => {
    if (!date) return '';
    const dateStr = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    
    if (dateStr < today) return 'bg-gray-100 text-gray-400';
    if (selectedDates.includes(dateStr)) return 'bg-[#C5A059] text-white';
    
    const price = getPriceForDate(date);
    if (price) {
      // Scala colore basata sul prezzo
      if (price.prezzo_notte >= 200) return 'bg-red-100 text-red-800';
      if (price.prezzo_notte >= 150) return 'bg-orange-100 text-orange-800';
      if (price.prezzo_notte >= 100) return 'bg-yellow-100 text-yellow-800';
      return 'bg-green-100 text-green-800';
    }
    
    return 'bg-white hover:bg-gray-50';
  };

  if (loading) {
    return (
      <AdminLayout title="Gestione Prezzi">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#C5A059]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestione Prezzi">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Calendario Prezzi</h2>
            <p className="text-gray-500 text-sm">Clicca sulle date per impostare i prezzi • Sync automatico con Beds24</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={importFromBeds24}
              disabled={importing}
              data-testid="import-beds24-btn"
            >
              {importing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importando...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Importa da Beds24</>
              )}
            </Button>
            <Button variant="outline" onClick={() => setBulkDialogOpen(true)} data-testid="add-periods-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Periodi
            </Button>
            {selectedDates.length > 0 && (
              <Button onClick={() => setPriceDialogOpen(true)} className="bg-[#C5A059] hover:bg-[#B08A3E]" data-testid="set-price-btn">
                <DollarSign className="w-4 h-4 mr-2" />
                Imposta Prezzo ({selectedDates.length} date)
              </Button>
            )}
          </div>
        </div>

        {/* Selettore Unità */}
        {units.length > 1 && (
          <div className="flex gap-2">
            {units.map(unit => (
              <Button
                key={unit.id}
                variant={selectedUnit?.id === unit.id ? 'default' : 'outline'}
                onClick={() => setSelectedUnit(unit)}
                className={selectedUnit?.id === unit.id ? 'bg-[#C5A059]' : ''}
                data-testid={`unit-selector-${unit.id}`}
              >
                {unit.nome}
              </Button>
            ))}
          </div>
        )}

        {/* Calendario */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-lg capitalize">{formatMonth(currentMonth)}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Header giorni settimana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Griglia giorni */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((date, idx) => {
                const price = date ? getPriceForDate(date) : null;
                const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);
                
                return (
                  <div
                    key={idx}
                    onClick={() => toggleDate(date)}
                    className={`
                      min-h-[70px] p-1 rounded-lg border cursor-pointer transition-all
                      ${getCellColor(date)}
                      ${date ? 'border-gray-200' : 'border-transparent'}
                      ${isWeekend ? 'font-semibold' : ''}
                    `}
                  >
                    {date && (
                      <>
                        <div className="text-sm">{date.getDate()}</div>
                        {price && (
                          <div className="text-xs font-bold mt-1">
                            €{price.prezzo_notte}
                          </div>
                        )}
                        {price?.soggiorno_minimo > 1 && (
                          <div className="text-[10px] text-gray-500">
                            min {price.soggiorno_minimo}n
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Legenda */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-xs">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-green-100"></div>
                <span>€0-99</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-yellow-100"></div>
                <span>€100-149</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-orange-100"></div>
                <span>€150-199</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-red-100"></div>
                <span>€200+</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-[#C5A059]"></div>
                <span>Selezionato</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Periodi Salvati */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#C5A059]" />
              Periodi Tariffari Configurati
            </CardTitle>
            <CardDescription>
              Questi prezzi sono sincronizzati automaticamente con Beds24, Airbnb e Booking.com
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pricePeriods.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Nessun periodo configurato. Seleziona delle date dal calendario per iniziare.
              </p>
            ) : (
              <div className="space-y-2">
                {pricePeriods.sort((a, b) => a.data_inizio.localeCompare(b.data_inizio)).map((period, idx) => (
                  <div 
                    key={period.id || idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`period-row-${period.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className="text-lg font-bold min-w-[80px] justify-center">
                        €{period.prezzo_notte}
                      </Badge>
                      <div>
                        <p className="font-medium">
                          {new Date(period.data_inizio).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                          {' → '}
                          {new Date(period.data_fine).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500">
                          Min. {period.soggiorno_minimo || 1} notti
                          {period.imported_from_beds24 && ' • Importato da Beds24'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        Beds24
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingPeriod({ ...period });
                          setEditDialogOpen(true);
                        }}
                        className="h-8 w-8 p-0 hover:bg-blue-100"
                        data-testid={`edit-period-${period.id}`}
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPeriodToDelete(period);
                          setDeleteConfirmOpen(true);
                        }}
                        className="h-8 w-8 p-0 hover:bg-red-100"
                        data-testid={`delete-period-${period.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog Modifica Periodo */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Periodo Tariffario</DialogTitle>
            </DialogHeader>
            {editingPeriod && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Data Inizio</Label>
                    <Input
                      type="date"
                      value={editingPeriod.data_inizio}
                      onChange={(e) => setEditingPeriod({ ...editingPeriod, data_inizio: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Data Fine</Label>
                    <Input
                      type="date"
                      value={editingPeriod.data_fine}
                      onChange={(e) => setEditingPeriod({ ...editingPeriod, data_fine: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Prezzo/Notte (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editingPeriod.prezzo_notte}
                      onChange={(e) => setEditingPeriod({ ...editingPeriod, prezzo_notte: e.target.value })}
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Min. Notti</Label>
                    <Input
                      type="number"
                      min="1"
                      value={editingPeriod.soggiorno_minimo || 1}
                      onChange={(e) => setEditingPeriod({ ...editingPeriod, soggiorno_minimo: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={updatePeriod}
                disabled={syncing}
                className="bg-[#C5A059] hover:bg-[#B08A3E]"
              >
                {syncing ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Salva e Sincronizza</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Conferma Eliminazione */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conferma Eliminazione</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare questo periodo tariffario?
              </p>
              {periodToDelete && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="font-semibold text-red-800">
                    €{periodToDelete.prezzo_notte}/notte
                  </p>
                  <p className="text-sm text-red-600">
                    {new Date(periodToDelete.data_inizio).toLocaleDateString('it-IT')} → {new Date(periodToDelete.data_fine).toLocaleDateString('it-IT')}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-3">
                ⚠️ Questa azione non può essere annullata. Il prezzo rimarrà configurato su Beds24 fino alla prossima sincronizzazione.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={deletePeriod}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Imposta Prezzo */}
        <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Imposta Prezzo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  Date selezionate: <strong>{selectedDates.length}</strong>
                </p>
                <p className="text-xs text-gray-500">
                  {selectedDates.length > 0 && `Dal ${selectedDates[0]} al ${selectedDates[selectedDates.length - 1]}`}
                </p>
              </div>
              
              <div>
                <Label>Prezzo per notte (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="120.00"
                  value={priceForm.price}
                  onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })}
                  className="text-lg"
                />
              </div>
              
              <div>
                <Label>Soggiorno minimo (notti)</Label>
                <Input
                  type="number"
                  min="1"
                  value={priceForm.minStay}
                  onChange={(e) => setPriceForm({ ...priceForm, minStay: e.target.value })}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="syncBeds24"
                  checked={priceForm.syncToBeds24}
                  onChange={(e) => setPriceForm({ ...priceForm, syncToBeds24: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="syncBeds24" className="cursor-pointer">
                  Sincronizza su Beds24 (Airbnb, Booking.com)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPriceDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={savePriceForDates}
                disabled={syncing}
                className="bg-[#C5A059] hover:bg-[#B08A3E]"
              >
                {syncing ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Salva e Sincronizza</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Bulk Prices */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Aggiungi Periodi Tariffari</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {bulkForm.periods.map((period, idx) => (
                <div key={idx} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">Periodo {idx + 1}</span>
                    <div className="flex gap-1">
                      {idx > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyPreviousPeriod(idx)}
                          title="Copia prezzo dal periodo precedente"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                      {bulkForm.periods.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBulkPeriod(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Data Inizio</Label>
                      <Input
                        type="date"
                        value={period.startDate}
                        onChange={(e) => {
                          const newPeriods = [...bulkForm.periods];
                          newPeriods[idx].startDate = e.target.value;
                          setBulkForm({ periods: newPeriods });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data Fine</Label>
                      <Input
                        type="date"
                        value={period.endDate}
                        onChange={(e) => {
                          const newPeriods = [...bulkForm.periods];
                          newPeriods[idx].endDate = e.target.value;
                          setBulkForm({ periods: newPeriods });
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Prezzo/Notte (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="120"
                        value={period.price}
                        onChange={(e) => {
                          const newPeriods = [...bulkForm.periods];
                          newPeriods[idx].price = e.target.value;
                          setBulkForm({ periods: newPeriods });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Min. Notti</Label>
                      <Input
                        type="number"
                        min="1"
                        value={period.minStay}
                        onChange={(e) => {
                          const newPeriods = [...bulkForm.periods];
                          newPeriods[idx].minStay = e.target.value;
                          setBulkForm({ periods: newPeriods });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addBulkPeriod}
                className="w-full border-dashed"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi Altro Periodo
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={saveBulkPrices}
                disabled={syncing}
                className="bg-[#C5A059] hover:bg-[#B08A3E]"
              >
                {syncing ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Salva Tutti e Sincronizza</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
